import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import {
  useIsAdmin,
  TasksBarIcon,
  TasksNavItem,
  ProjectsNav,
} from '@internal/plugin-waadoo-core';
import { adminOnlyNavItem } from './adminNav';
import { SidebarLogo } from './SidebarLogo';
import { SidebarUserBlock } from './SidebarUserBlock';
import HomeIcon from '@material-ui/icons/Home';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { frenchNavTitles } from '@waadoo/backstage-i18n';
import { NavEntry, NavNotifications, NavSearch } from './RailItems';
import {
  CompactNavController,
  SidebarCompactToggle,
} from './SidebarCompactToggle';

/** `item`, for the admin tier only — see `adminNav.ts` and `useIsAdmin.ts`. */
function AdminOnlyNavItem(props: { item: JSX.Element | null }) {
  const admin = useIsAdmin();
  return adminOnlyNavItem(props.item, admin);
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      // NavEntry: a SidebarItem, its name as tooltip in the compact menu.
      const nav = navItems.withComponent(item => (
        <NavEntry
          icon={item.icon}
          href={item.href}
          text={frenchNavTitles[item.title] ?? item.title}
        />
      ));

      // Skipped items
      nav.take('page:search'); // Using search modal instead
      // Two entries the product does not use, dropped the same way: taking an
      // item is what removes it from `rest()`, and both plugins stay installed
      // because the catalog entity page pulls them in.
      //
      // The ids are those of the *page* extensions — `NavItemBag` keys on the
      // page node, not on the nav item — namely the nameless `PageBlueprint` of
      // the `api-docs` and `techdocs` plugins. `take()` answers null for an id
      // that does not exist, silently, so these two are read from the installed
      // packages rather than guessed.
      nav.take('page:api-docs');
      nav.take('page:techdocs');
      // Since 1.55 these three plugins declare a title and an icon, so their
      // pages land in `rest()`. Notifications already has its own item at the
      // bottom (with the unread counter), the graph is reached from the
      // Relations card, and registering a component by hand does not fit a
      // catalog fed by templates.
      nav.take('page:notifications');
      nav.take('page:catalog-graph');
      nav.take('page:catalog-import');
      // « Tâches » (components/tasks), drawn by its own item for its badge:
      // taken from the bag itself, which `nav` shares, so that it never
      // falls into `rest()` either.
      const tasks = navItems.take('page:waadoo-tasks');

      return (
        // No expansion on hover: the compact menu (compactNav.ts) is the
        // unpinned drawer, and it stays 72 px wide until the reader unfolds
        // it — the edge's tab, `[`, Ctrl+B or Paramètres.
        <Sidebar disableExpandOnHover>
          <CompactNavController />
          <SidebarLogo />
          {/* The tab on the menu's edge, level with the logo, next to it in
              the tab order. Desktop only, like the user block: not a group,
              so the phone's bottom bar leaves it out. */}
          <SidebarCompactToggle />
          {/* The user's name, sign-out and the theme switch. Desktop only: on
              a phone core-components' MobileSidebar keeps the SidebarGroups
              alone, and « Compte » below, a group with a `to`, opens the
              Settings page rather than its children — the page whose
              appearance card (plugin-user-settings) offers the same theme
              choice. */}
          <SidebarUserBlock />
          <SidebarDivider />
          {/* On a phone the groups are the bottom bar (core-components'
              MobileSidebar), in this order: Accueil, Tâches, Rechercher,
              Menu, Compte — each an icon over its label (styles.css, the
              phone rules). The labels are the buttons' accessible names and
              the title of an open group, hence in French. On a desktop a
              group renders its children alone, in place: the two empty ones
              first add nothing, and « Rechercher » stays the first item. */}
          {/* The bottom bar's way home. Empty on purpose: on a desktop it
              adds nothing to the sidebar, where « Accueil » is the first item
              of the menu below. */}
          <SidebarGroup label="Accueil" icon={<HomeIcon />} to="/" />
          {/* « Tâches » one tap away on a phone, its badge over the icon.
              Empty as well: on a desktop it is the menu's own entry, which
              the phone still has under « Menu ». */}
          {tasks && (
            <SidebarGroup
              label={tasks.title}
              icon={<TasksBarIcon icon={tasks.icon} />}
              to={tasks.href}
            />
          )}
          <SidebarGroup label="Rechercher" icon={<SearchIcon />} to="/search">
            <NavSearch />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            {/* The home page (components/home/plugin.tsx), first: « Accueil ». */}
            {nav.take('page:waadoo-home')}
            {/* Admins only: the others reach their projects through
                « Accueil » and « Projets ». Taken in every case, so it never
                falls into `rest()`; the route stays open to everyone. */}
            <AdminOnlyNavItem item={nav.take('page:catalog')} />
            {nav.take('page:scaffolder')}
            {/* Right after « Créer », for everyone: what was launched there.
                In this group, so that a phone reaches it through « Menu ». */}
            {tasks && (
              <TasksNavItem
                href={tasks.href}
                title={tasks.title}
                icon={tasks.icon}
              />
            )}
            {/* « Coûts plateforme » (components/projectCosts), admins only,
                like the catalog: the OVH bill split by project. Taken in
                every case, so it never falls into `rest()`; the page sends
                anyone else home. */}
            <AdminOnlyNavItem item={nav.take('page:waadoo-costs')} />
            <SidebarDivider />
            <ProjectsNav />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NavNotifications />
          <SidebarDivider />
          <SidebarGroup
            label="Compte"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {/* Admins only, like the catalog: a developer tool (the tree of
                the frontend's extensions), of no use to a project's people.
                The route stays open, with nothing sensitive behind it. */}
            <AdminOnlyNavItem item={nav.take('page:app-visualizer')} />

            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
